import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Typography, Divider } from "antd";
import { purchaseOrderRecordnWithDetailsApi } from "../../../../../apiservice/purchase";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone } from "../../../../../lib/functions";
import "./purchaseOrderVoucher.css"
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../../Auth/authSelectors";

const { Title, Text } = Typography;

const PurchaseOrderVoucherBody = ({ pur_o_id, institution, printMode = false, showStamp = false,
  showSignature = false, setVoucherNo, setAccName, setSupplierEmail }) => {
  const [data, setData] = useState(null);
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency;
  const isBonusItem = (item) => Number(item.item_rate) === 0;
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";

  useEffect(() => {
    if (!pur_o_id) return;
    purchaseOrderRecordnWithDetailsApi({ pur_o_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];
        setData(record);
        // important guard for printMode
        if (!printMode && setVoucherNo) {
          setVoucherNo(record.pur_order_no);
          setAccName(record.acc_name);
        }

        if (record.email_address) {
          setSupplierEmail?.(record.email_address);
        }
      }
    });
  }, [pur_o_id]);

  if (!data) return null;

  // Real enforcement point for both Download and Print: this component
  // renders in printMode for the hidden react-to-print layout (Print) AND
  // for the /print/purchase-order/:id route the backend Puppeteer service
  // screenshots (Download) — blocking the render here blocks both actions
  // regardless of button state, not just a disabled prop that could be
  // bypassed. Normal on-screen viewing (printMode=false) is untouched.
  if (printMode && data.approval_status === "pending") {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Title level={4}>Pending Approval</Title>
        <Text>This purchase order is pending approval and cannot be downloaded or printed yet.</Text>
      </div>
    );
  }

  const isIndividual = data.is_cal_type === "individual";

  const uniqueItemsMap = new Map();

  (data.details || []).forEach(item => {
    const key = `${item.pur_d_id}-${item.item_id}`;
    if (!uniqueItemsMap.has(key)) {
      uniqueItemsMap.set(key, item);
    }
  });

  const items = Array.from(uniqueItemsMap.values()).map(item => ({
    ...item,
    qty_display:
      item?.item_qty + " " + item?.unit_symbol +
      (item?.conversion > 1
        ? ", " + item?.retail_qty + " " + item?.base_unit_name
        : ""),
    done_qty_display:
      item?.done_item_qty + " " + item?.unit_symbol +
      (item?.conversion > 1
        ? ", " + item?.done_retail_qty + " " + item?.base_unit_name
        : ""),
  }));

  const serviceCost = (data.otherServiceTrans || []).reduce(
    (sum, row) => sum + Number(row.tran_amount || 0),
    0
  );

  const hasBonusItems = items.some(isBonusItem);



  // calculate grand total
  const totals = items.reduce(
    (acc, d) => {
      const qty = d.item_qty || d.pur_o_qty || 0;
      const rate = Number(d.item_rate || 0);

      let base = qty * rate;

      const discountAmt =
        (Number(d.item_discount_per || 0) / 100) * base;

      const afterDiscount = base - discountAmt;

      const taxAmt =
        (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;

      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  // const serviceCost = Number(data.other_service_cost || 0);
  const grandTotal = isIndividual
    ? totals.grandTotal + serviceCost
    : (data?.total_amount || 0) + serviceCost;

  const totalTax = isIndividual
    ? totals.taxTotal
    : Number(data?.total_tax || 0);

  const cgst = isIndia ? totalTax / 2 : 0;
  const sgst = isIndia ? totalTax / 2 : 0;


  const columns = isIndividual
    ? [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        render: (text, record) => (
          <div className="item-cell">
            <span className="bonus-badge-container">
              {text}
              {isBonusItem(record) && (
                <span className="bonus-badge">BONUS</span>
              )}
            </span>
            {record.item_description && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2, whiteSpace: "pre-wrap" }}>
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
        width: 50
      },
      { title: "Per", dataIndex: "unit_name", align: "right", width: 70 },
      { title: "Qty", dataIndex: "qty_display", align: "right", width: 80 },
      { title: "Disct%", dataIndex: "item_discount_per", align: "right", width: 75 },
      ...(!isGSTDisabled
        ? [
          { title: `${taxLabel} `, dataIndex: "item_tax_per", align: "right", width: 70 },
        ]
        : []),
      {
        title: "Total",
        align: "right",
        render: (_, d) => {
          let qty = d.item_qty || d.item_qty || 0;
          let rate = Number(d.item_rate || 0);
          let total = qty * rate;
          total -= (Number(d.item_discount_per) / 100) * total;
          total += (Number(d.item_tax_per) / 100) * total;
          return total.toFixed(2);
        },
        width: 90
      },
    ]
    : [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        render: (text, record) => (
          <div className="item-cell">
            <span className="bonus-badge-container">
              {text}
              {isBonusItem(record) && (
                <span className="bonus-badge">BONUS</span>
              )}
            </span>
            {record.item_description && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2, whiteSpace: "pre-wrap" }}>
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
      { title: "Per", dataIndex: "per_unit_symbol", align: "right" },
      { title: "Order Quantity", dataIndex: "qty_display", align: "right" },
      { title: "Sold Quantity", dataIndex: "done_qty_display", align: "right" },
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
    ];

  return (
    <div className="voucher-section invoice-v2">
      {/* Header */}

      <Row justify="space-between" align="top" className="invoice-header">
        {/* <Col>
            <div className="company-block">
              
              <div>
                <Title level={4}>{authInfo?.institution_name}</Title>
                <Text>{authInfo?.address}</Text>
              </div>
            </div>
          </Col> */}


        <Col flex="0 0 320px" className="bill-to" style={{ marginTop: "58px" }}>
          <Text strong>Supplier/ Creditor Code: &nbsp;{data.acc_code}</Text>
          <br />
          <Text strong>Name: &nbsp;{data.acc_name}</Text>
          {/* <br />
          <Text >Institution: &nbsp;{data.institution_name}</Text> */}
          <br />
          <Text className="wrap-text">Address: &nbsp; {data.address}</Text>
          <Text>Contact No: &nbsp; {data.contact_no}</Text>
        </Col>
        <Col flex="320px" className="invoice-title">
          <Title level={2}>PURCHASE ORDER</Title>
          <Text strong>Voucher No: {data.pur_order_no}</Text>
          <br />
          <Text>Order Date: {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}</Text>
          <br />
          <Text>Order By: {data.full_name?.split(" ")[0]}</Text>
          <br />
          <Text className="wrap-text">Address : {institution?.street_address}</Text>
          {institution?.phone_no && <Text>Contact No : &nbsp; {institution?.phone_no}</Text>}
          <br />
          <Text>Payment Terms: {data.payment_terms}</Text>
          <br />
          {/* <Text>{institution?.country?.trim().toLowerCase() ===
            "united arab emirates" && (institution?.subscription_type.toLowerCase().includes("pharma") || institution?.subscription_type.toLowerCase().includes("pharmabooks"))
            ? "Rep.: "
            : "Account Manager: "} {data.acc_mgr}</Text>
          <br /> */}
          {institution?.vat_license && <><Text>
            {institution?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {institution?.vat_license}
          </Text><br /></>}
          {data.licence_no && <Text>License No : {data.licence_no}</Text>}
        </Col>

      </Row>


      {/* Items Table */}
      <Table
        className="invoice-items"
        dataSource={items}
        columns={columns}
        pagination={false}
        data-testid="purchase-order-voucher-table"
        rowKey={(record) => `${record.pur_d_id}-${record.item_id}`}
        size="small"
        bordered={false}
        rowClassName={(record) =>
          isBonusItem(record) ? "bonus-row" : ""
        }
        summary={() => {
          if (isIndividual) {
            return (
              <Table.Summary.Row>
                {/* <Table.Summary.Cell colSpan={5} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{grandTotal.toFixed(2)}</b>
                </Table.Summary.Cell> */}
              </Table.Summary.Row>
            );
          }
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.sub_total || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Discount
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_discount || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {/* {isIndia ? (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={5} align="right">
                      CGST
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {cgst.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={5} align="right">
                      SGST
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {sgst.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              ) : (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={5} align="right">
                    {taxLabel}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {totalTax.toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )} */}
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  {taxLabel}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {totalTax.toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {/* <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{Number(grandTotal).toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row> */}
            </>
          );
        }}
      />

      {hasBonusItems && <Text style={{ display: "block", fontSize: 11, color: "#666", marginLeft: 10, marginTop: 10 }}>
        *Bonus items are supplied free of cost and not included in taxable value.
      </Text>}

      {/* Footer */}
      <Divider />
      <Row>
        <Col span={16}>
          {/* <Text>Invoice Due : {numberFormat(data.due_total || 0)}</Text>
    <br />
    <Text>
      Current Customer Due : {numberFormat(data.due_amount || 0)}
    </Text> */}
        </Col>

        <Col span={8} style={{ textAlign: "right" }}>
          <div>
            {/* Quantity */}
            {/* <div style={{ display: "flex", textAlign: "left" }}>
              <Text>Quantity Total :</Text>
              <Text>
                ({items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0)})
              </Text>
            </div> */}

            {/* Sub Total */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Sub Total : </Text>
              <Text>
                {numberFormat(isIndividual ? totals.subTotal : data.sub_total || 0)}
              </Text>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Discount :</Text>
              <Text>
                {numberFormat(isIndividual ? totals.discountTotal : data.total_discount)}
              </Text>
            </div>

            {/* {isIndia ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text>CGST :</Text>
                  <Text>{numberFormat(cgst)}</Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text>SGST :</Text>
                  <Text>{numberFormat(sgst)}</Text>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>{taxLabel} :</Text>
                <Text>{numberFormat(totalTax)}</Text>
              </div>
            )} */}

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>{taxLabel} :</Text>
              <Text>{numberFormat(totalTax)}</Text>
            </div>

            {serviceCost > 0 && <>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Other Expenses :</Text>
                <Text>{numberFormat(serviceCost)}</Text>
              </div>
            </>}

            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Grand Total ({institution?.currency}): </Text>
              <Text strong>{numberFormat(data.total_amount)}</Text>
            </div>

            {/* Paid */}
            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Paid : {institution?.currency}</Text>
              <Text strong>{numberFormat(data.paid_total || 0)}</Text>
            </div>

            {/* Due */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 2,
              }}
            >
              <Text strong>Due : {institution?.currency}</Text>
              <Text strong>{numberFormat(data.due_total || 0)}</Text>
            </div>
          </div>
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(grandTotal, data?.currency || institution?.currency)}
          </Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>


      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">

          <div className="footer-right-group">

            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}

          </div>
        </div>
      )}

      {data?.show_terms_conditions === "yes" &&
        data?.payment_terms_description && (
          <div className="po-payment-terms-page">
            <div className="po-payment-terms-content">
              <Title level={4} style={{ textAlign: "center" }}>
                Payment Terms
              </Title>

              <ul>
                {data.payment_terms_description
                  .split("\n")
                  .filter((line) => line.trim())
                  .map((line, idx) => (
                    <li key={idx}>
                      {line.replace(/^•\s*/, "")}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
    </div>
  );
};

export default PurchaseOrderVoucherBody;
